// Shadowed on 2020-11-10 - up to date
import React from 'react'
import PropTypes from 'prop-types'
import { StaticQuery, graphql, Link } from 'gatsby'

import { DocumentHead, StickyNav, SubscribeOverlay, SubscribeSuccess } from 'gatsby-theme-try-ghost/src/components/common'
import { BodyClass } from 'gatsby-theme-try-ghost/src/components/common/helpers'

import useOptions from 'gatsby-theme-try-ghost/src/utils/use-options'
import { useLang, get } from 'gatsby-theme-try-ghost/src/utils/use-lang'

// Styles
import 'gatsby-theme-try-ghost/src/styles/screen.css'
import 'gatsby-theme-try-ghost/src/styles/fluid.css'
import 'gatsby-theme-try-ghost/src/styles/dark-mode.css'
import 'gatsby-theme-try-ghost/src/styles/pagination.css'
import 'gatsby-theme-try-ghost/src/styles/prism.css'
import 'gatsby-theme-try-ghost/src/styles/toc.css'

import '../../styles/medeinos.css'; // Medeinos: added custom styles

/**
* Main layout component
*
* The Layout component wraps around each page and template.
* It also provides the header, footer as well as the main
* styles, and meta data for each page.
*
*/
const DefaultLayout = ({ data, header, children, isHome, isPost, sticky, previewPosts, author, tags, page, errorClass, parsedQuery, overlay }) => {
    const { basePath } = useOptions()
    const text = get(useLang())
    const config = data.site.siteMetadata
    const site = data.allGhostSettings.edges[0].node
    const title = text(`SITE_TITLE`, site.title)
    const bodyClass = BodyClass({ isHome: isHome, isPost: isPost, author: author, tags: tags, page: page })

    const twitterUrl = site.twitter && `https://twitter.com/${site.twitter.replace(/^@/, ``)}`
    const facebookUrl = site.facebook && `https://www.facebook.com/${site.facebook.replace(/^\//, ``)}`

    errorClass = errorClass || ``

    return (
        <React.Fragment>
            {/* Dark Mode shadows DocumentHead */}
            <DocumentHead site={site} className={bodyClass} parsedQuery={parsedQuery} />

            <div className="site-wrapper">
                {/* The main header section on top of the screen */}
                {header}
                {/* The main content area */}
                <main ref={isHome && sticky && sticky.anchorRef} id="site-main" className={`site-main outer ${errorClass}`}>
                    {/* All the main content gets inserted here, index.js, post.js */}
                    {children}
                </main>

                {/* For sticky nav bar */}
                { isHome && <StickyNav className={`site-nav ${sticky && sticky.state.currentClass}`} overlay={overlay}/>}

                {/* Links to Previous/Next posts */}
                {previewPosts}

                {/* The footer at the very bottom of the screen */}
                <footer className="site-footer outer" >
                    <div className="site-footer-content inner">
                        <section className="copyright">
                            <a href={config.siteUrl}>{title}</a> &copy; {new Date().getFullYear()}
                        </section>

                        <nav className="site-footer-nav" style={{
                            flexWrap: 'wrap',
                            justifyContent: 'center'
                        }}>
                            <Link to={basePath}>{text(`LATEST_POSTS`)}</Link>
                            {/* <a target="_blank" rel="noopener" href="https://krautuvele.medeinos.lt">Medeinos Krautuvėlė</a> */}
                            <Link to="/augalu-katalogas/a">Augalų Katalogas</Link> {/* Medeinos: Added catalog link */}
                            <a target="_blank" rel="noopener noreferrer" href="https://www.inaturalist.org/observations/edita_medeina">iNaturalist stebėjimai</a> {/* Medeinos: added shop */}    
                            { site.facebook && <a href={facebookUrl} target="_blank" rel="noopener noreferrer">Facebook</a> }
                            {/* { site.twitter && <a href={twitterUrl} target="_blank" rel="noopener noreferrer">Twitter</a> } */}
                            <Link to="/apie/">Apie</Link> {/* Medeinos: Added about link */}
                            {/* Medeinos: removed Jamify link */}
                        </nav>
                    </div>
                </footer>
            </div>

            <SubscribeSuccess parsedQuery={parsedQuery} title={title} />

            {/* The big email subscribe modal content */}
            <SubscribeOverlay overlay={overlay} />

        </React.Fragment>
    )
}

DefaultLayout.propTypes = {
    header: PropTypes.node.isRequired,
    children: PropTypes.node.isRequired,
    previewPosts: PropTypes.node,
    isHome: PropTypes.bool,
    isPost: PropTypes.bool,
    data: PropTypes.shape({
        allGhostSettings: PropTypes.object.isRequired,
        site: PropTypes.object.isRequired,
    }).isRequired,
    sticky: PropTypes.shape({
        anchorRef: PropTypes.object.isRequired,
        state: PropTypes.shape({
            currentClass: PropTypes.string,
        }),
    }),
    author: PropTypes.object,
    tags: PropTypes.arrayOf(
        PropTypes.object.isRequired,
    ),
    page: PropTypes.object,
    errorClass: PropTypes.string,
    parsedQuery: PropTypes.object,
    overlay: PropTypes.object,
}

const DefaultLayoutSettingsQuery = props => (
    <StaticQuery
        query={graphql`
            query GhostSettingsShadowed {
                allGhostSettings {
                    edges {
                        node {
                            ...GhostSettingsFields
                        }
                    }
                }
                site {
                    siteMetadata {
                        ...SiteMetadataFields
                    }
                }
            }
        `}
        render={data => <DefaultLayout data={data} {...props} />}
    />
)

export default DefaultLayoutSettingsQuery